import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { getDates } from './getDates';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

export function parseDateStringValue(value: string | Date): Date {
  if (value instanceof Date) {
    return value;
  }

  return dayjs(value).toDate();
}

function toLocalDateKey(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isInWeekRange(date: Date, value: Date | null) {
  if (!value) return false;

  const { startDate, endDate } = getDates(value);

  return (
    dayjs(date).isSameOrBefore(endDate) && dayjs(date).isSameOrAfter(startDate)
  );
}

export function isDayWithinInterval(
  day: Date,
  intervalStart: Date,
  intervalEnd: Date,
) {
  const dayKey = toLocalDateKey(day);

  return (
    dayKey >= toLocalDateKey(intervalStart) &&
    dayKey <= toLocalDateKey(intervalEnd)
  );
}

export function isWeekWithinInterval(
  weekAnchor: Date,
  intervalStart: Date,
  intervalEnd: Date,
) {
  const { startDate: weekStart, endDate: weekEnd } = getDates(weekAnchor);

  return (
    toLocalDateKey(weekStart) <= toLocalDateKey(intervalEnd) &&
    toLocalDateKey(weekEnd) >= toLocalDateKey(intervalStart)
  );
}

type WeekSelectorInterval = { start: Date; end: Date } | null;

export function getWeekSelectorDatePickerBounds(
  weekInterval: WeekSelectorInterval,
  referenceDate: Date = new Date(),
) {
  const { endDate: endOfCurrentWeek } = getDates(referenceDate);

  const endOfCurrentWeekKey = toLocalDateKey(endOfCurrentWeek);
  const maxDateKey = weekInterval
    ? toLocalDateKey(weekInterval.end) < endOfCurrentWeekKey
      ? toLocalDateKey(weekInterval.end)
      : endOfCurrentWeekKey
    : endOfCurrentWeekKey;

  const maxDateSource =
    maxDateKey === endOfCurrentWeekKey ? endOfCurrentWeek : weekInterval!.end;

  return {
    endOfCurrentWeek,
    minDate: weekInterval
      ? dayjs(weekInterval.start).startOf('day').toDate()
      : undefined,
    maxDate: dayjs(maxDateSource).endOf('day').toDate(),
  };
}

export function isWeekSelectorDaySelectable(
  day: Date,
  weekInterval: WeekSelectorInterval,
  endOfCurrentWeek: Date,
) {
  if (toLocalDateKey(day) > toLocalDateKey(endOfCurrentWeek)) {
    return false;
  }

  if (!weekInterval) {
    return true;
  }

  return isDayWithinInterval(day, weekInterval.start, weekInterval.end);
}

export function isDayKey<T extends object>(
  key: string,
): key is Extract<keyof T, string> {
  return key.startsWith('day');
}
